import { PlusIcon, PencilIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import type { ContractTemplate } from '@shared/types';

interface TemplateListProps {
  templates: ContractTemplate[];
  loading: boolean;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  onCreateNew: () => void;
  onEdit: (template: ContractTemplate) => void;
  onToggleActive: (id: string, currentStatus: boolean) => void;
}

const typeColors: Record<string, string> = {
  'SOW': 'bg-blue-100 text-blue-700',
  'NDA': 'bg-purple-100 text-purple-700',
  'PSA': 'bg-green-100 text-green-700',
  'MSA': 'bg-orange-100 text-orange-700',
  'Service Agreement': 'bg-cyan-100 text-cyan-700',
  'License Agreement': 'bg-pink-100 text-pink-700',
  'Other': 'bg-gray-100 text-gray-700',
};

export function TemplateList({
  templates,
  loading,
  typeFilter,
  onTypeFilterChange,
  onCreateNew,
  onEdit,
  onToggleActive,
}: TemplateListProps) {
  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Templates</h2>
          <button
            onClick={onCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            New Template
          </button>
        </div>

        <select
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Types</option>
          <option value="SOW">Statement of Work</option>
          <option value="NDA">Non-Disclosure Agreement</option>
          <option value="PSA">Professional Services Agreement</option>
          <option value="MSA">Master Service Agreement</option>
          <option value="Service Agreement">Service Agreement</option>
          <option value="License Agreement">License Agreement</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : templates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No templates found</div>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className="p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => onEdit(template)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        typeColors[template.templateType] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {template.templateType}
                    </span>
                    <span className="text-xs text-gray-500">v{template.version}</span>
                    {template.isActive ? (
                      <CheckCircleIcon className="w-4 h-4 text-green-600" title="Active" />
                    ) : (
                      <XCircleIcon className="w-4 h-4 text-gray-400" title="Inactive" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <p className="text-xs text-gray-500">
                      📁 {template.sharepointFileName || template.sharepointPath}
                    </p>
                    {template.sharepointFileId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        ✓ Linked
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {template.hasScope && (
                      <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                        Scope
                      </span>
                    )}
                    {template.hasDeliverables && (
                      <span className="px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded">
                        Deliverables
                      </span>
                    )}
                    {template.hasMilestones && (
                      <span className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">
                        Milestones
                      </span>
                    )}
                    {template.hasPaymentSchedule && (
                      <span className="px-2 py-0.5 text-xs bg-yellow-50 text-yellow-700 rounded">
                        Payments
                      </span>
                    )}
                    {template.supportsRecurringPayments && (
                      <span className="px-2 py-0.5 text-xs bg-pink-50 text-pink-700 rounded">
                        Recurring
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(template);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleActive(template.id, template.isActive);
                    }}
                    className={`p-2 rounded-md ${
                      template.isActive
                        ? 'text-orange-600 hover:bg-orange-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                    title={template.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {template.isActive ? (
                      <XCircleIcon className="w-4 h-4" />
                    ) : (
                      <CheckCircleIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

